// based on https://github.com/morrys/wora/blob/master/packages/apollo-offline/src/ApolloClientIDB.ts
// it is customized to not throw errors when idb is unavailable (firefox private mode), and to use idb-keyval to interact
// with idb (which has built-in safari 14 fix)

import type { ICacheStorage, CacheOptions } from '@wora/cache-persist'
import { ApolloClientOffline } from '@wora/apollo-offline/lib/ApolloClientOffline'
import type { InMemoryCacheConfig } from 'apollo-cache-inmemory'
import { ApolloCache } from '@wora/apollo-cache'
import type { ApolloClientIDBOptions } from '@wora/apollo-offline/lib/ApolloOfflineTypes'
import type { UseStore } from 'idb-keyval'
import { createStore, keys, get, set, del } from 'idb-keyval'

export class ApolloClientIDB {
  public static create(
    config: ApolloClientIDBOptions,
    options: {
      idbName: string
      cacheOptions?: InMemoryCacheConfig
      persistOptions?: CacheOptions
      offlineStoreOptions?: CacheOptions
    }
  ): ApolloClientOffline {
    // returns the interface for writing/reading to an idb
    function makeStorage(name: string): ICacheStorage {
      if (typeof window === 'undefined') {
        return
      }

      let _store: UseStore

      function getStore() {
        if (_store) {
          return _store
        }

        // can't create multiple stores on same idbName, instead we'll create a unique idbName
        // see: https://github.com/jakearchibald/idb-keyval/blob/main/custom-stores.md#defining-a-custom-database--store-name
        _store = createStore(options.idbName + '-' + name, 'data')

        return _store
      }

      return {
        getAllKeys: () =>
          keys(getStore())
            // if idb is unavailable, this throws, so we'll return an empty array
            .catch(() => []) as Promise<any[]>,
        getItem: (key) => get(key, getStore()),
        removeItem: (key) => del(key, getStore()),
        setItem: (key, value) => set(key, value, getStore())
      }
    }

    const { cacheOptions, persistOptions = {}, offlineStoreOptions = {} } = options

    // normal apollo cache
    const idbStore: CacheOptions = {
      serialize: false,
      prefix: null,
      storage: makeStorage('store'),
      ...persistOptions
    }

    // cache for the offline mutations
    const idbOffline: CacheOptions = {
      serialize: false,
      prefix: null,
      storage: makeStorage('offline'),
      ...offlineStoreOptions
    }

    const cache = new ApolloCache(cacheOptions, idbStore)
    return new ApolloClientOffline({ ...config, cache }, idbOffline)
  }
}

export default ApolloClientIDB
